'use client'
import { useEffect, useMemo } from 'react'
import { useBooking } from './bookingContext'
import { SelectedAvailability, useSelectedAvailability } from './useSelectedAvailability'
import { AvailabilityMeetingPlace, GetMeetingLinkQuery } from '@/core/graphql/types'
import { useGetMeetingLink } from '@/shared/api/meeting-link'
import { ConsultationMode } from './bookingTypes'

import { useSearchParams } from '@/lib/next-shim'

type MeetingLinkData = GetMeetingLinkQuery['meetingLink'] | undefined

export function useSyncContext(meetingLinkSlug: string) {
  const selectedAvailability = useSelectedAvailability(meetingLinkSlug)
  const { meetingLink } = useGetMeetingLink(meetingLinkSlug)
  const searchParams = useSearchParams()

  useSyncAvailability(selectedAvailability)
  useSyncFormTemplate(selectedAvailability)
  useRequiresPayment(selectedAvailability, meetingLink)
  // useAutoSelectSpeciality(meetingLink)
  useSyncSpecialityFromUrl(meetingLink, searchParams)
}

function useSyncSpecialityFromUrl(meetingLink: MeetingLinkData, searchParams: URLSearchParams) {
  const { state, dispatch } = useBooking()
  const specialityParam = searchParams.get('speciality')

  useEffect(() => {
    if (specialityParam && meetingLink?.specialities && !state.selectedSpeciality) {
      const found = meetingLink.specialities.find(
        (s) => s.name.toLowerCase() === specialityParam.toLowerCase() || s.code.toLowerCase() === specialityParam.toLowerCase()
      )
      if (found) {
        console.log('🔄 [SYNC] Selecting speciality from URL:', found.name)
        dispatch({
          type: 'SET_AVAILABILITY',
          payload: {
            availabilityId: '',
            speciality: found.name,
          },
          goToNextStep: true,
        })
      }
    }
  }, [specialityParam, meetingLink, state.selectedSpeciality, dispatch])
}

function useAutoSelectSpeciality(meetingLink: MeetingLinkData) {
  const { state, dispatch } = useBooking()

  useEffect(() => {
    // Only auto-select if no speciality is selected yet
    if (meetingLink?.specialities?.length === 1 && !state.selectedSpeciality) {
      const speciality = meetingLink.specialities[0]
      console.log('🔄 [SYNC] Auto-selecting single speciality:', speciality.name)
      dispatch({
        type: 'SET_AVAILABILITY',
        payload: {
          availabilityId: '',
          speciality: speciality.name,
        },
        goToNextStep: true,
      })
    }
  }, [meetingLink, state.selectedSpeciality, dispatch])
}

function useSyncFormTemplate(selectedAvailability: SelectedAvailability) {
  const { dispatch } = useBooking()
  // Set form template flag
  useEffect(() => {
    if (selectedAvailability?.formTemplate?._id) {
      console.log('🔄 [SYNC] Setting has form template flag in context')
      dispatch({ type: 'SET_HAS_FORM_TEMPLATE', payload: true })
    }
  }, [selectedAvailability, dispatch])
}

function useRequiresPayment(selectedAvailability: SelectedAvailability, meetingLink: MeetingLinkData) {
  const { state, dispatch } = useBooking()

  const requiresPayment = useMemo(() => {
    // If immediate consultation mode
    if (state.consultationMode === ConsultationMode.Immediate) {
      const config = meetingLink?.immediateConsultationConfig
      // Check if payment is required and we have a valid price and payment receiver with Stripe
      const hasPaymentConfig = config?.requirePaymentUpfront === true
      const hasValidPrice = (config?.price ?? 0) > 0
      const hasStripe = !!config?.paymentReceiver?.stripe

      console.log('🔄 [PAYMENT] Immediate consultation payment check:', {
        requirePaymentUpfront: config?.requirePaymentUpfront,
        price: config?.price,
        hasStripe,
        result: hasPaymentConfig && hasValidPrice && hasStripe,
      })

      return hasPaymentConfig && hasValidPrice && hasStripe
    }

    const paymentConfig = selectedAvailability?.paymentConfig

    // Default to false if paymentConfig is not defined
    if (!paymentConfig) {
      console.log('🔄 [PAYMENT] Payment redirection check: No payment config found')
      return false
    }

    const requiresPayment = paymentConfig.requiresPayment === true && (paymentConfig.basePrice ?? 0) > 0 && !!paymentConfig.paymentReceiver?.stripe

    console.log('🔄 [PAYMENT] Payment redirection check:', {
      requiresPayment,
      paymentConfig: paymentConfig,
      basePrice: paymentConfig.basePrice,
      paymentReceiver: paymentConfig.paymentReceiver,
      existingRequiresPayment: state.requiresPayment,
    })

    switch (selectedAvailability.meetingPlace) {
      case AvailabilityMeetingPlace.SecondOpinion:
        // Require payment for second opinions after form submission
        return requiresPayment && !!state.formSubmissionId
      case AvailabilityMeetingPlace.AtHome:
      case AvailabilityMeetingPlace.Center:
      case AvailabilityMeetingPlace.VisioConference:
      default:
        // For physical appointments, use the payment config setting
        return requiresPayment
    }
  }, [selectedAvailability, meetingLink, state.consultationMode, state.formSubmissionId])

  // Set requiresPayment in context when it changes
  useEffect(() => {
    if (requiresPayment !== state.requiresPayment) {
      console.log('🔄 [PAYMENT] Updating requiresPayment in context:', requiresPayment)
      dispatch({ type: 'SET_REQUIRES_PAYMENT', payload: requiresPayment })
    }
  }, [dispatch, requiresPayment, state.requiresPayment])
}

function useSyncAvailability(selectedAvailability: SelectedAvailability) {
  const { state, dispatch } = useBooking()
  useEffect(() => {
    // Only sync availability if NOT in immediate mode
    if (selectedAvailability && state.consultationMode !== ConsultationMode.Immediate) {
      dispatch({
        type: 'SET_AVAILABILITY',
        payload: {
          availabilityId: selectedAvailability._id,
        },
      })
    }
  }, [dispatch, selectedAvailability, state.consultationMode])
}
